import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Botao } from './base';
import { TextoCortado } from './texto-cortado';

// Pilha de camadas abertas: o Esc fecha só a de cima (drawer com modal dentro, por exemplo).
const pilha: symbol[] = [];
/** Rodada rfil-3 (ROB-M2): o `overflow` de antes da primeira camada. Por camada, duas fechando no mesmo desenho deixavam a trava presa no `body`. */
let overflowOriginal = '';

/**
 * Review r28 robustez (B1): quem precisa saber se há camada aberta (o aviso fixo de nova versão, que cobria o rodapé e o
 * campo de Modais e Drawers) assina a pilha (`useSyncExternalStore`).
 */
const ouvintesCamadas = new Set<() => void>();
export const haCamadaAberta = () => pilha.length > 0;
export function assinarCamadas(f: () => void) {
  ouvintesCamadas.add(f);
  return () => void ouvintesCamadas.delete(f);
}
const avisarCamadas = () => ouvintesCamadas.forEach((f) => f());

/** Valor do `overflow` do `body` com camada aberta (review rlb-2, VIS-M1). */
export const TRAVA_ROLAGEM = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('overflow', 'clip') ? 'clip' : 'hidden';

export function useEsc(aberto: boolean, fechar: () => void) {
  // Rodada rfil-2 (VIS-B5): o `aoFechar` costuma mudar a cada desenho. Com ele nas dependências, a camada de baixo saía e entrava de novo na
  // pilha e passava a ser a do topo: o Esc do pop-up fechava a gaveta atrás dele. A posição na pilha só depende de abrir e fechar.
  const atual = useRef(fechar);
  atual.current = fechar;
  useEffect(() => {
    if (!aberto) return;
    const eu = Symbol('camada');
    if (!pilha.length) overflowOriginal = document.body.style.overflow;
    pilha.push(eu);
    avisarCamadas();
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || pilha[pilha.length - 1] !== eu) return;
      e.stopPropagation();
      atual.current();
    };
    window.addEventListener('keydown', h);
    // Review rlb-2 (VIS-M1): `clip`, não `hidden`. Com a casca fixa (`html` sem rolagem), `hidden` no `body` fazia dele um
    // contêiner de rolagem e a barra superior descia a altura da faixa de ambiente enquanto a camada estava aberta. `clip`
    // corta sem criar contêiner e, na casca antiga (`html` visível), passa à janela como `hidden`: a página por baixo
    // continua sem rolar. Sem suporte a `clip`, fica o `hidden` de antes.
    document.body.style.overflow = TRAVA_ROLAGEM;
    return () => {
      window.removeEventListener('keydown', h);
      pilha.splice(pilha.indexOf(eu), 1);
      avisarCamadas();
      if (!pilha.length) document.body.style.overflow = overflowOriginal;
    };
  }, [aberto]);
}

// Review r66 (VIS-B1): `:disabled` (e não `[disabled]`) pega também o campo dentro de `<fieldset disabled>`.
const FOCAVEIS = 'a[href], button:not(:disabled), input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';
/** Campo que o foco inicial pode receber: nunca um mascarado pelo modo discreto (o foco revelaria o valor, r10 visual M2). */
const CAMPOS = 'input:not([type="hidden"]):not([aria-hidden]):not([data-mascarado]), select, textarea:not([data-mascarado])';

/** Camadas com foco preso (modal, drawer, tour), da mais antiga para a do topo. Só a do topo prende o foco. */
const camadas: symbol[] = [];

function focaveis(el: HTMLElement) {
  return [...el.querySelectorAll<HTMLElement>(FOCAVEIS)].filter((x) => x.offsetParent !== null || x === document.activeElement);
}

/** Foco inicial (r6 visual B5): campo ou botão habilitado e visível; nunca o Fechar, nunca campo desabilitado ou escondido. */
const INICIAL = 'input:not([type="hidden"]):not(:disabled):not([aria-hidden]):not([tabindex="-1"]):not([data-mascarado]), select:not(:disabled), textarea:not(:disabled):not([data-mascarado]), button:not([data-fechar]):not(:disabled):not([tabindex="-1"])';
function primeiroInicial(el: HTMLElement) {
  return [...el.querySelectorAll<HTMLElement>(INICIAL)].find((x) => x.offsetParent !== null && x.getAttribute('aria-disabled') !== 'true') ?? null;
}

/**
 * Região fora das camadas que pode ter o foco sem ser puxada de volta (r6 visual B8): o fechar do aviso (toast) fica alcançável
 * com o modal aberto pelo atalho F8. O Tab dentro dela volta para a camada do topo.
 */
export const LIVRE_DO_TRAP = '[data-livre-do-trap]';

/**
 * Prende o foco na camada do topo. O Tab é tratado no `document` (o foco pode ter caído no body quando um botão
 * ficou desabilitado) e, se o foco sai da camada por qualquer outro caminho, volta para ela. O elemento é lido do
 * `ref` a cada evento (o tour troca de cartão entre os passos). Ao fechar, devolve o foco a quem o tinha antes.
 */
export function usePrenderFoco(ref: React.RefObject<HTMLElement | null>, ativo: boolean, opcoes: { focoInicial?: boolean; devolver?: boolean } = {}) {
  const { focoInicial = true, devolver = true } = opcoes;
  useEffect(() => {
    const el0 = ref.current;
    if (!ativo || !el0) return;
    const anterior = document.activeElement as HTMLElement | null;
    const eu = Symbol('camada');
    camadas.push(eu);
    const topo = () => camadas[camadas.length - 1] === eu;
    const atual = () => ref.current;
    if (focoInicial) {
      (primeiroInicial(el0) ?? el0).focus();
    }
    // Conteúdo que chega depois (formulário que espera os dados): o primeiro campo recebe o foco se o usuário ainda não interagiu.
    let obs: MutationObserver | undefined;
    let interagiu = false;
    const marcar = () => (interagiu = true);
    if (focoInicial && !el0.querySelector(CAMPOS)) {
      el0.addEventListener('keydown', marcar);
      el0.addEventListener('pointerdown', marcar);
      obs = new MutationObserver(() => {
        if (interagiu) return obs?.disconnect();
        const campo = [...el0.querySelectorAll<HTMLElement>(CAMPOS)].find((c) => !(c as HTMLInputElement).disabled && c.offsetParent !== null);
        if (campo) {
          campo.focus();
          obs?.disconnect();
        }
      });
      obs.observe(el0, { childList: true, subtree: true });
      setTimeout(() => obs?.disconnect(), 5000);
    }
    const prender = (e: KeyboardEvent) => {
      const el = atual();
      if (e.key !== 'Tab' || !el || !topo()) return;
      const itens = focaveis(el);
      if (!itens.length) return e.preventDefault();
      const [primeiro, ultimo] = [itens[0]!, itens[itens.length - 1]!];
      const ativoEl = document.activeElement;
      if (!el.contains(ativoEl) || ativoEl === el) {
        e.preventDefault();
        (e.shiftKey ? ultimo : primeiro).focus();
      } else if (e.shiftKey && ativoEl === primeiro) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && ativoEl === ultimo) {
        e.preventDefault();
        primeiro.focus();
      }
    };
    // Último elemento focado dentro da camada (r6 visual A1): o foco que cai no body (lista da busca que fechou, botão que ficou
    // desabilitado, elemento removido) volta para onde estava, e não para o primeiro controle (isso prendia o Tab entre o Fechar e
    // a busca). Se ele sumiu, vai para o anterior a ele que ainda existe na ordem de Tab, ou para o primeiro.
    let ultimoFocado: HTMLElement | null = null;
    let ordem: HTMLElement[] = [];
    const lembrar = (e: FocusEvent) => {
      const el = atual();
      const alvo = e.target as HTMLElement | null;
      if (!el || !topo() || !alvo || alvo === el || !el.contains(alvo)) return;
      ultimoFocado = alvo;
      ordem = focaveis(el);
    };
    const recuperar = () => {
      setTimeout(() => {
        const el = atual();
        if (!topo() || !el?.isConnected) return;
        const a = document.activeElement;
        if (a && a !== document.body && (el.contains(a) || a.closest(LIVRE_DO_TRAP))) return;
        const itens = focaveis(el);
        if (ultimoFocado && itens.includes(ultimoFocado)) return ultimoFocado.focus();
        const i = ultimoFocado ? ordem.indexOf(ultimoFocado) : -1;
        const antes = i > 0 ? ordem.slice(0, i).reverse().find((x) => itens.includes(x)) : undefined;
        (antes ?? itens[0] ?? el).focus();
      }, 0);
    };
    document.addEventListener('keydown', prender);
    document.addEventListener('focusin', lembrar);
    document.addEventListener('focusout', recuperar);
    return () => {
      obs?.disconnect();
      el0.removeEventListener('keydown', marcar);
      el0.removeEventListener('pointerdown', marcar);
      document.removeEventListener('keydown', prender);
      document.removeEventListener('focusin', lembrar);
      document.removeEventListener('focusout', recuperar);
      const i = camadas.indexOf(eu);
      if (i >= 0) camadas.splice(i, 1);
      if (devolver && anterior?.isConnected) anterior.focus();
    };
  }, [ativo, ref, focoInicial, devolver]);
}

export function useFocoInicial(aberto: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  usePrenderFoco(ref, aberto);
  return ref;
}

export function Modal({ aberto, aoFechar, titulo, children, rodape, largura = 'md' }: { aberto: boolean; aoFechar: () => void; titulo: ReactNode; children: ReactNode; rodape?: ReactNode; largura?: 'md' | 'lg' }) {
  const { t } = useTranslation();
  useEsc(aberto, aoFechar);
  const ref = useFocoInicial(aberto);
  const id = useId();
  if (!aberto) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-veu p-0 sm:items-center sm:p-4" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        tabIndex={-1}
        className={`flex max-h-dvh focus:outline-none w-full flex-col rounded-t-2xl border border-borda bg-sup-1 shadow-2xl sm:rounded-2xl ${largura === 'lg' ? 'sm:max-w-3xl' : 'sm:max-w-lg'}`}
      >
        {/* div, não header/footer (r8 visual B9): fora de seção, header vira um segundo landmark banner (e footer, contentinfo). */}
        <div data-cabecalho className="flex items-center justify-between gap-3 border-b border-borda px-5 py-4">
          <h2 id={id} className="text-base font-semibold">
            {titulo}
          </h2>
          <button type="button" data-fechar aria-label={t('comum.fechar')} onClick={aoFechar} className="-mr-2 flex size-11 shrink-0 items-center justify-center rounded-lg text-tinta-3 hover:bg-sup-2 hover:text-tinta-1 md:size-9">
            <X className="size-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {rodape && <div data-rodape className="flex flex-wrap justify-end gap-2 border-t border-borda px-5 py-3">{rodape}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function Drawer({ aberto, aoFechar, titulo, children, rodape, largura = 'md' }: { aberto: boolean; aoFechar: () => void; titulo: ReactNode; children: ReactNode; rodape?: ReactNode; largura?: 'md' | 'lg' }) {
  const { t } = useTranslation();
  useEsc(aberto, aoFechar);
  const ref = useFocoInicial(aberto);
  const id = useId();
  if (!aberto) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end bg-veu" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      {/* div, não aside/header/footer (r8 visual B9): aside com role="dialog" não é permitido e o header dentro dele virava um
          segundo banner no celular. Na impressão a gaveta não sai (como antes, quando era aside). */}
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        tabIndex={-1}
        data-sem-impressao
        className={`flex h-full w-full focus:outline-none flex-col border-l border-borda bg-sup-1 shadow-2xl ${largura === 'lg' ? 'sm:max-w-3xl' : 'sm:max-w-xl'}`}
      >
        <div data-cabecalho className="flex items-center justify-between gap-3 border-b border-borda px-5 py-4">
          <TextoCortado como="h2" id={id} className="text-base font-semibold">
            {titulo}
          </TextoCortado>
          <button type="button" data-fechar aria-label={t('comum.fechar')} onClick={aoFechar} className="-mr-2 flex size-11 shrink-0 items-center justify-center rounded-lg text-tinta-3 hover:bg-sup-2 hover:text-tinta-1 md:size-9">
            <X className="size-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {rodape && <div data-rodape className="flex flex-wrap justify-end gap-2 border-t border-borda px-5 py-3">{rodape}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function Confirmar({ aberto, aoFechar, aoConfirmar, titulo, texto, perigoso, carregando }: { aberto: boolean; aoFechar: () => void; aoConfirmar: () => void; titulo: string; texto: ReactNode; perigoso?: boolean; carregando?: boolean }) {
  const { t } = useTranslation();
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={titulo}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {t('comum.cancelar')}
          </Botao>
          <Botao variante={perigoso ? 'perigo' : 'primario'} carregando={carregando} onClick={aoConfirmar}>
            {t('comum.confirmar')}
          </Botao>
        </>
      }
    >
      <div className="text-sm text-tinta-2">{texto}</div>
    </Modal>
  );
}
